import { Button, Group, Stack, Text } from '@mantine/core';
import { TextareaHTMLAttributes, useState } from 'react';

import {
  Batch,
  DeclarationCustomsFlowType,
  DeclarationHighValueGroupType,
  DeclarationStatus,
  EnvironmentCountries,
  getTotal,
  TemplateType,
} from '@e-origin/shared';

import { MultiLangCodes } from '@components';
import { useInvalidateAllReleasedDeclarations } from '@hooks/declaration';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { classes } from '@styles';

const getDeclarationsToInvalidate = (batch: Batch, country: EnvironmentCountries): React.ReactElement => {
  const released = getTotal(batch, DeclarationStatus.RELEASED, DeclarationCustomsFlowType.POST);
  const invalidationPending = batch.statistics.invalidationPending ?? 0;
  let totalAccepted = 0;

  if (country === EnvironmentCountries.NL) {
    const acceptedInPost = getTotal(batch, DeclarationStatus.ACCEPTED, DeclarationCustomsFlowType.POST);
    const acceptedInPre = getTotal(batch, DeclarationStatus.ACCEPTED, DeclarationCustomsFlowType.PRE);
    totalAccepted = acceptedInPost + acceptedInPre;
  }

  return (
    <div>
      <div>You are going to invalidate:</div>
      {released > 0 && (
        <div>
          {released - invalidationPending} declaration(s) in status {DeclarationStatus.RELEASED}
        </div>
      )}
      {totalAccepted > 0 && (
        <div>
          {totalAccepted - invalidationPending} declaration(s) in status {DeclarationStatus.ACCEPTED}
        </div>
      )}
      <div>
        Total {released + totalAccepted} of {batch.statistics.total}.
      </div>
    </div>
  );
};

interface InvalidateBatchProps {
  afterCreate(): void;
  onHide(): void;
  updatedBatch?: Batch;
}

export const InvalidateBatch: React.FC<InvalidateBatchProps> = ({ afterCreate, onHide, updatedBatch }) => {
  const { data: settings } = useSettings();
  const isFR = settings?.country === EnvironmentCountries.FR;
  const [invalidationRequestText, setInvalidationRequestText] = useState('');
  const [invalidationMotivation, setInvalidationMotivation] = useState('');

  const declarationGroup = updatedBatch?.templateType === TemplateType.HIGH_VALUE_H1 ? DeclarationHighValueGroupType.H1 : undefined;
  const codelistSystems = useCodeListSystem({ group: declarationGroup, isExport: updatedBatch?.isExport });

  const invalidateAllReleasedDeclarationsMutation = useInvalidateAllReleasedDeclarations();

  const invalidateReleasedDeclarations = () => {
    invalidateAllReleasedDeclarationsMutation.mutate({
      batchId: updatedBatch._id,
      reason: invalidationRequestText,
      invalidationMotivation: isFR ? invalidationMotivation : undefined,
    });
    afterCreate();
    onHide();
  };

  const handleInvalidateBatchChange: TextareaHTMLAttributes<HTMLTextAreaElement>['onChange'] = (event) => {
    setInvalidationRequestText(event.target.value);
  };

  return (
    <>
      <Stack gap={0}>
        <Text pt={8} component="label">
          Invalidate batch
        </Text>
        {getDeclarationsToInvalidate(updatedBatch, settings?.country)}
        {isFR && (
          <MultiLangCodes
            value={invalidationMotivation}
            label="Invalidation motivation"
            code="RECORD_NIS_MOTIFS_INVALIDATION"
            system={codelistSystems}
            onChange={(value) => setInvalidationMotivation(value)}
          />
        )}
        <textarea
          value={invalidationRequestText}
          onChange={handleInvalidateBatchChange}
          disabled={false}
          placeholder="Invalidation reason"
          className={classes.inputBorder}
          style={{
            margin: '10px 0',
            borderRadius: '5px',
            padding: '10px',
          }}
        />
      </Stack>
      <Group justify="center">
        <Button type="button" key="cancel-batch-modal-btn" onClick={onHide} size="lg" variant="transparent">
          Cancel
        </Button>
        <Button
          type="button"
          key="create-batch-modal-btn"
          onClick={invalidateReleasedDeclarations}
          disabled={!invalidationRequestText.trim() || (isFR && !invalidationMotivation.trim())}
          variant="filled"
          size="lg"
        >
          Request invalidation
        </Button>
      </Group>
    </>
  );
};
